/**
 * Helper functions to define whether browser is supported or not.
 */
import UAParser from "ua-parser-js";
import { SessionStorageKeys } from "utils/storage";

export type BrowserInfo = Omit<
  ReturnType<typeof UAParser>,
  "browser" | "os" | "device"
> & {
  browser: ParsedBrowser;
  os: ParsedOS;
  device: ParsedDevice;
  additionalBrowserInfo: string;
  supported: boolean;
};

type SupportedBrowser = {
  name: string;
  version: string;
};

type ParsedBrowser = {
  name?: string;
  version?: string;
  supported: boolean;
};

type ParsedOS = {
  name?: string;
  version?: string;
  supported: boolean;
};

type ParsedDevice = {
  model?: string;
  type?: string;
  vendor?: string;
};

// Read and write persistent browser information.
export function acceptBrowser(accepted: boolean) {
  sessionStorage.setItem(SessionStorageKeys.browserAccepted, String(accepted));
}

export function shouldAskAboutBrowser() {
  return sessionStorage.getItem(SessionStorageKeys.browserAccepted) === null;
}

// Browser version matrix. We may want to move that into a config module
// or JSON setting somewhere more accessible.
// For a complete list of browsers that can be detected from the user agents string:
// https://github.com/faisalman/ua-parser-js#methods
const supportedBrowsers: SupportedBrowser[] = [
  { name: "Chrome", version: "69.0.0" }, // Jan 2018
  { name: "Firefox", version: "76.0.0" }, // May 2018
  { name: "Safari", version: "11.0.0" }, // Sep 2018 (adds WebRTC support for Tokbox)
  { name: "Edge", version: "80.0.0" }, // May 2019 (based on Chromium)
  { name: "Electron", version: "" }, // used for e2e tests
];

// OS matrix of supported systems. We may want to move that into a config
// module or JSON setting somewhere more accessible.
// For a complete list of OSs that can be detected from the user agents string:
// https://github.com/faisalman/ua-parser-js#methods
const supportedOSs = [
  "Mac OS",
  "Fedora",
  "Debian",
  "Ubuntu",
  "Linux",
  "Windows 7",
  "Windows 8",
  "Windows 8.1",
  "Windows 10",
  "Windows",
  "iOS",
  // "Chrome OS",
  "Android OS",
  "Android",
];

// Global browser information for reuse.
let browser: BrowserInfo | undefined;

/**
 * Helper function to compare two semantic version strings of the form "MM.mm.pp".
 * Return 0 if both version strings are the same; -1 if the first string is less
 * than the second one; and 1 if the first string is greater than the second one.
 * TODO a prettier way would be to normalize the version strings to, e.g. MMM.mmmmm.ppppp
 * format, remove the . and treat the version numbers as plain integers.
 *
 * @param versionStr - is a semantic version string.
 * @param otherVersionStr - is a semantic version string.
 * @returns comparison result
 */
function compareVersions(
  versionStr: string | undefined,
  otherVersionStr: string
): number {
  // Semantic version string to three integer values.
  // TODO handle version strings of the form "MM.mm" and "MM".
  const [major, minor, patch] = getVersionParts(versionStr);
  const [otherMajor, otherMinor, otherPatch] = getVersionParts(otherVersionStr);

  // Now compare the version values.
  if (major < otherMajor) {
    return -1;
  } else if (major > otherMajor) {
    return 1;
  } else if (minor < otherMinor) {
    return -1;
  } else if (minor > otherMinor) {
    return 1;
  } else if (patch < otherPatch) {
    return -1;
  } else if (patch > otherPatch) {
    return 1;
  } else {
    return 0;
  }
}

function getVersionParts(versionStr: string | undefined): [number, number, number] {
  const [major = Number.NaN, minor = Number.NaN, patch = Number.NaN] = (versionStr ?? "")
    .split(".")
    .map(part => parseInt(part, 10));

  return [major, minor, patch];
}

function formatAdditionalBrowserInfo(ua: ReturnType<typeof UAParser>) {
  if (!ua.browser.name) {
    return "?";
  }

  return `${ua.browser.name} ver${ua.browser.version ?? "?"} on OS ${ua.os.name ?? "?"} (${ua.device.model ?? "unknown device"})`;
}

/**
 * Detect and return information about the current browser. Note that this
 * information is unreliable as it can be spoofed by the client; however, we
 * trust that it's ok or else the client who spoofs the UA string will have a
 * bad time.
 */
export default function getBrowserInfo(): BrowserInfo {
  if (browser) {
    return browser;
  }

  // Determine and add `supported` flags, first for browser and os, then the
  // overall combination for all.
  const ua = UAParser();
  const isSupportedBrowser = supportedBrowsers.some(supportedBrowser => {
    return (
      ua.browser.name === supportedBrowser.name &&
      compareVersions(ua.browser.version, supportedBrowser.version) >= 0
    );
  });
  const isSupportedOS =
    ua.os.name !== undefined && ua.os.name !== null && supportedOSs.includes(ua.os.name);

  browser = {
    ...ua,
    browser: {
      ...ua.browser,
      supported: isSupportedBrowser,
    },
    os: {
      ...ua.os,
      supported: isSupportedOS,
    },
    additionalBrowserInfo: formatAdditionalBrowserInfo(ua),
    supported: isSupportedBrowser && isSupportedOS,
  };

  return browser;
}
